import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import HeaderBack from "@/components/dashboard/Header/HeaderBack";
import MotionText from '@/components/common/Motion/MotionText';
import { usePageSetup } from '@/hooks/usePageSetup';

import {
  PiGasPump, PiLetterCircleP, PiPhone,
  PiRoadHorizon, PiWrench, PiTire,
  PiMagnifyingGlass, PiBank,
  PiTShirt, PiCalculator, PiChartLineDown,
  PiToolbox, PiHeartbeat, PiShieldCheck,
  PiNotebook, PiGraduationCap, PiNavigationArrow,
  PiDeviceMobile,
  PiPackage as PiDeliveryBox,
  PiSquaresFour, PiDevices,
  PiCalculator as PiTax, PiUsers, PiCertificate,
  PiFiles, PiCellSignalFull, PiSprayBottle,
  PiHardHat, PiAppWindow, PiCarProfile, PiDrop, PiScales,
  PiSimCard,
  PiSquare,
  PiDropHalf,
} from 'react-icons/pi';

import ButtonLink from "@/components/common/Button/ButtonLink";
import MotionDiv from '@/components/common/Motion/MotionDiv';

export const expenseIconMap: Record<string, React.ReactNode> = {
  // Vehicle Expenses
  fuel: <PiGasPump className="h-6 w-6 text-zinc-900" />,
  maintenance: <PiWrench className="h-6 w-6 text-zinc-900" />,
  car_wash: <PiDrop className="h-6 w-6 text-zinc-900" />,
  parking: <PiLetterCircleP className="h-6 w-6 text-zinc-900" />,
  tolls: <PiRoadHorizon className="h-6 w-6 text-zinc-900" />,
  depreciation: <PiChartLineDown className="h-6 w-6 text-zinc-900" />,
  rental: <PiCarProfile className="h-6 w-6 text-zinc-900" />,
  car_payment: <PiBank className="h-6 w-6 text-zinc-900" />,
  tires: <PiTire className="h-6 w-6 text-zinc-900" />,
  registration: <PiFiles className="h-6 w-6 text-zinc-900" />,
  inspection: <PiMagnifyingGlass className="h-6 w-6 text-zinc-900" />,
  oil_change: <PiDrop className="h-6 w-6 text-zinc-900" />,
  repairs: <PiWrench className="h-6 w-6 text-zinc-900" />,
  insurance: <PiShieldCheck className="h-6 w-6 text-zinc-900" />,
  // Commission Fees
  platform_commission: <PiBank className="h-6 w-6 text-zinc-900" />,
  payment_processing: <PiScales className="h-6 w-6 text-zinc-900" />,
  service_charges: <PiScales className="h-6 w-6 text-zinc-900" />,
  transaction_fees: <PiCalculator className="h-6 w-6 text-zinc-900" />,
  // Communication Expenses
  phone: <PiPhone className="h-6 w-6 text-zinc-900" />,
  internet: <PiCellSignalFull className="h-6 w-6 text-zinc-900" />,
  data_plan: <PiDeviceMobile className="h-6 w-6 text-zinc-900" />,
  software_subscriptions: <PiAppWindow className="h-6 w-6 text-zinc-900" />,
  navigation: <PiNavigationArrow className="h-6 w-6 text-zinc-900" />,
  // Equipment and Supplies
  work_clothes: <PiTShirt className="h-6 w-6 text-zinc-900" />,
  safety_equipment: <PiHardHat className="h-6 w-6 text-zinc-900" />,
  tools: <PiToolbox className="h-6 w-6 text-zinc-900" />,
  delivery_equipment: <PiDeliveryBox className="h-6 w-6 text-zinc-900" />,
  phone_accessories: <PiDevices className="h-6 w-6 text-zinc-900" />,
  cleaning_supplies: <PiSprayBottle className="h-6 w-6 text-zinc-900" />,
  // Taxes and Fees
  income_tax: <PiTax className="h-6 w-6 text-zinc-900" />,
  inverse_vat: <PiScales className="h-6 w-6 text-zinc-900" />,
  social_security: <PiHeartbeat className="h-6 w-6 text-zinc-900" />,
  license_fees: <PiCertificate className="h-6 w-6 text-zinc-900" />,
  permits: <PiFiles className="h-6 w-6 text-zinc-900" />,
  // Other Expenses
  office_supplies: <PiNotebook className="h-6 w-6 text-zinc-900" />,
  banking_fees: <PiBank className="h-6 w-6 text-zinc-900" />,
  professional_services: <PiUsers className="h-6 w-6 text-zinc-900" />,
  training: <PiGraduationCap className="h-6 w-6 text-zinc-900" />,
  miscellaneous: <PiSquaresFour className="h-6 w-6 text-zinc-900" />,
  // Parent Categories (groups)
  equipment_and_supplies: <PiToolbox className="h-6 w-6 text-zinc-900" />,
  other_expenses: <PiSquare className="h-6 w-6 text-zinc-900" />,
  taxes_and_fees: <PiScales className="h-6 w-6 text-zinc-900" />,
  vehicle_expenses: <PiGasPump className="h-6 w-6 text-zinc-900" />,
  commission_fees: <PiToolbox className="h-6 w-6 text-zinc-900" />,
  communication_expenses: <PiSimCard className="h-6 w-6 text-zinc-900" />,
}

export const ExpensesMemoPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('expenses.memo.title'))

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack />
      <div className="flex flex-col px-5 pb-[7rem] gap-6">
        <MotionText
          as="h1"
          className="text-[2.5rem] font-hubot font-black uppercase leading-none"
        >
          {t('expenses.memo.title')}
        </MotionText>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.memo.platformCommission')}
        </MotionText>

        <MotionText
          as='p'
          className='text-sm text-zinc-700 mb-2'
        >
          {t('expenses.memo.platformCommissionRule')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-3'>
          <MemoItem
            icon={<PiBank className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.boltCommission')} />
        </MotionDiv>
        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.memo.vehicleExpenses')}
        </MotionText>
        <MotionText
          as='p'
          className='text-sm text-zinc-700'
        >
          {t('expenses.memo.vehicleExpensesRule1')}
        </MotionText>
        <MotionText
          as='p'
          className='text-sm text-zinc-700'
        >
          {t('expenses.memo.vehicleExpensesRule2')}
        </MotionText>
        <MotionText
          as='p'
          className='text-sm text-zinc-700 mb-2'
        >
          {t('expenses.memo.vehicleExpensesRule3')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-3'>
          <MemoItem
            icon={<PiGasPump className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.fuel')} />
          <MemoItem
            icon={<PiWrench className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.repairMaintenance')} />
          <MemoItem
            icon={<PiShieldCheck className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.insurance')} />
          <MemoItem
            icon={<PiDrop className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.washing')} />
          <MemoItem
            icon={<PiDropHalf className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.adblue')} />
        </MotionDiv>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.memo.communication')}
        </MotionText>

        <MotionText
          as='p'
          className='text-sm text-zinc-700 mb-2'
        >
          {t('expenses.memo.communicationRule')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-3'>
          <MemoItem
            icon={<PiSimCard className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.mobileInvoices')} />
          <MemoItem
            icon={<PiAppWindow className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.internet')} />
        </MotionDiv>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.memo.taxes')}
        </MotionText>

        <MotionText
          as='p'
          className='text-sm text-zinc-700 mb-2'
        >
          {t('expenses.memo.taxesRule')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-3'>
          <MemoItem
            icon={<PiHeartbeat className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.psd')} />
          <MemoItem
            icon={<PiUsers className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.vsd')} />
          <MemoItem
            icon={<PiTax className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.pollutionTax')} />
          <MemoItem
            icon={<PiRoadHorizon className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.memo.roadTax')} />
        </MotionDiv>

        <motion.div
          className="absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: "easeOut"
          }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonLink to={'/'}>
            {t('common.ok')}
          </ButtonLink>
        </motion.div>
      </div >
    </div >
  );
};

const MemoItem = ({ icon, title }: { icon?: React.ReactNode; title: string }) => {
  return <MotionDiv className='flex flex-row items-center'>
    {icon && <div
      className="flex items-center justify-center min-w-12 min-h-12 rounded-full bg-zinc-100 mr-5">
      {icon}
    </div>
    }
    <div className='flex flex-col'>
      <h3 className="font-medium text-base text-black">{title}</h3>
    </div>
  </MotionDiv>
}

export default ExpensesMemoPage;